const GuidedFormPage = (function () {
  // All selectors are scoped to the GuidedFormPage template to avoid affecting other pages.
  const selectors = {
    page: ".js-guided-form-page",
    categoryButton: ".js-guided-form-category-button",
    categoryPanel: ".js-guided-form-category-panel",
    showFormButton: ".js-guided-form-show-form",
    selectedButton: ".selected",
    formContainer: ".guided-form-page__form",
    recaptchaElement: ".FormRecaptcha",
  } as const;

  // Entry point: wire GuidedFormPage-specific behavior when the page exists.
  function init(): void {
    const $page = $(selectors.page);
    if (!$page.length) {
      return;
    }

    // Let EPi Forms initialize all form elements (including reCAPTCHA) on load.
    // Visibility is controlled by the wrapper's hidden attribute instead.
    $page.find(`${selectors.formContainer} form`).removeClass("form-hidden");

    bindCategoryButtonClick($page);
    bindShowFormButtonClick($page);
  }

  // Handle top category button clicks: selected state, FAQ panel toggle, hidden field sync.
  function bindCategoryButtonClick($page: JQuery<HTMLElement>): void {
    $page.on("click", selectors.categoryButton, function (event) {
      // Prevent the generic form button handler from applying selection styles.
      event.stopImmediatePropagation();

      const $clickedButton = $(this);
      setSingleSelectedButton($page, selectors.categoryButton, $clickedButton);

      const selectedCategoryValue = getDataString($clickedButton, "help-category-value");
      if (!selectedCategoryValue) {
        return;
      }

      toggleCategoryPanels($page, selectedCategoryValue);
      setHelpCategoryFieldValue($page, selectedCategoryValue);
    });
  }

  // Handle "show form" button clicks inside a category panel.
  function bindShowFormButtonClick($page: JQuery<HTMLElement>): void {
    $page.on("click", selectors.showFormButton, function (event) {
      event.stopImmediatePropagation();

      const $clickedButton = $(this);
      setSingleSelectedButton($page, selectors.showFormButton, $clickedButton);
      showForm($page, getDataString($clickedButton, "form-target"));
    });
  }

  // Keep only one button selected within the given selector group.
  function setSingleSelectedButton(
    $page: JQuery<HTMLElement>,
    buttonSelector: string,
    $clickedButton: JQuery<HTMLElement>,
  ): void {
    $page.find(`${buttonSelector}${selectors.selectedButton}`).removeClass("selected");
    $clickedButton.addClass("selected");
  }

  // Safe helper for reading string values from data-* attributes.
  function getDataString($element: JQuery<HTMLElement>, dataKey: string): string {
    return String($element.data(dataKey) || "");
  }

  // Show the FAQ panel that matches the selected help category and hide the rest.
  function toggleCategoryPanels(
    $page: JQuery<HTMLElement>,
    selectedValue: string,
  ): void {
    $page.find(selectors.categoryPanel).each(function () {
      const $panel = $(this);
      const panelCategoryValue = String($panel.data("help-category-value") || "");
      const isSelectedPanel = panelCategoryValue === selectedValue;

      $panel.prop("hidden", !isSelectedPanel);
    });
  }

  // Write selected category into the hidden form field identified by integration key.
  function setHelpCategoryFieldValue(
    $page: JQuery<HTMLElement>,
    value: string,
  ): void {
    const integrationKey = getDataString($page, "help-category-integration-key");
    if (!integrationKey) {
      return;
    }

    const hiddenInputSelector = `input[type="hidden"][data-integration-key="${integrationKey}"]`;
    const $hiddenField = $page
      .find(selectors.formContainer)
      .find(hiddenInputSelector)
      .first();

    if (!$hiddenField.length) {
      return;
    }

    $hiddenField.val(value).trigger("change");
  }

  // Reveal the form container and ensure form internals are visible/initialized.
  function showForm($page: JQuery<HTMLElement>, formTargetId: string): void {
    if (!formTargetId) {
      return;
    }

    const $formTarget = $page.find(`#${formTargetId}`).first();
    if (!$formTarget.length) {
      return;
    }

    $formTarget.prop("hidden", false);
    $formTarget.removeAttr("hidden");
    $formTarget.find("form").removeClass("form-hidden");
    syncRecaptcha($formTarget);
  }

  // Keep reCAPTCHA usable after the form is revealed dynamically.
  // Resets existing widget when present; otherwise renders a new widget once.
  function syncRecaptcha($formTarget: JQuery<HTMLElement>): void {
    const $recaptchaElement = $formTarget.find(selectors.recaptchaElement).first();
    if (!$recaptchaElement.length) {
      return;
    }

    const grecaptchaInstance = (window as typeof window & {
      grecaptcha?: {
        render: (container: Element, parameters: { sitekey: string }) => number;
        reset: (optWidgetId?: number) => void;
      };
    }).grecaptcha;

    if (!grecaptchaInstance) {
      return;
    }

    const recaptchaContainer = $recaptchaElement.get(0);
    if (!recaptchaContainer) {
      return;
    }

    const widgetId = Number($recaptchaElement.data("widget-id"));
    if (!Number.isNaN(widgetId)) {
      grecaptchaInstance.reset(widgetId);
      return;
    }

    if (recaptchaContainer.querySelector("iframe")) {
      grecaptchaInstance.reset();
      return;
    }

    const siteKey = String($recaptchaElement.data("epiforms-sitekey") || "");
    if (!siteKey) {
      return;
    }

    // The container must be empty for grecaptcha.render.
    recaptchaContainer.innerHTML = "";
    const newWidgetId = grecaptchaInstance.render(recaptchaContainer, {
      sitekey: siteKey,
    });
    $recaptchaElement.attr("data-widget-id", String(newWidgetId));
  }

  return {
    init,
  };
})();

export default GuidedFormPage;
